/**
 * Gift API
 *
 * Mock gift service for challenge gifting.
 */

import type {
  Gift,
  GiftPermission,
  GiftRequest,
  GiftRecipientSearchResult,
  GiftPermissionType,
  Challenge,
  Community,
  CommunityMembership,
  ApiResponse,
  UUID,
  User,
} from '../types'
import { mockDb, delay } from './mockDb'
import { success, error } from './client'

// Session key (shared)
const SESSION_KEY = 'docurious-session'

function getCurrentUserId(): string | null {
  const stored = localStorage.getItem(SESSION_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored).userId
  } catch {
    return null
  }
}

/**
 * Gift API
 */
export const giftApi = {
  // ============================================================================
  // Gifts
  // ============================================================================

  /**
   * Get sent gifts
   */
  async getSentGifts(): Promise<ApiResponse<Gift[]>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const gifts = mockDb
      .findWhere<Gift>('gifts', (g) => g.senderUserId === userId)
      .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())

    // Populate challenge and recipient
    const populatedGifts = gifts.map((gift) => ({
      ...gift,
      challenge: mockDb.findById<Challenge>('challenges', gift.challengeId) ?? undefined,
      recipient: gift.recipientUserId
        ? (() => {
            const user = mockDb.findById<User>('users', gift.recipientUserId)
            return user ? { id: user.id, displayName: user.displayName } : undefined
          })()
        : undefined,
    }))

    return success(populatedGifts)
  },

  /**
   * Get received gifts
   */
  async getReceivedGifts(): Promise<ApiResponse<Gift[]>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const gifts = mockDb
      .findWhere<Gift>('gifts', (g) => g.recipientUserId === userId)
      .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())

    // Populate challenge and sender
    const populatedGifts = gifts.map((gift) => ({
      ...gift,
      challenge: mockDb.findById<Challenge>('challenges', gift.challengeId) ?? undefined,
      sender: (() => {
        const user = mockDb.findById<User>('users', gift.senderUserId)
        return user ? { id: user.id, displayName: user.displayName, avatarUrl: undefined } : undefined
      })(),
    }))

    return success(populatedGifts)
  },

  /**
   * Get pending (unaccepted) gifts
   */
  async getPendingGifts(): Promise<ApiResponse<Gift[]>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const gifts = mockDb
      .findWhere<Gift>(
        'gifts',
        (g) => g.recipientUserId === userId && g.status === 'pending'
      )
      .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())

    // Populate challenge and sender
    const populatedGifts = gifts.map((gift) => ({
      ...gift,
      challenge: mockDb.findById<Challenge>('challenges', gift.challengeId) ?? undefined,
      sender: (() => {
        const user = mockDb.findById<User>('users', gift.senderUserId)
        return user ? { id: user.id, displayName: user.displayName, avatarUrl: undefined } : undefined
      })(),
    }))

    return success(populatedGifts)
  },

  /**
   * Send a gift
   */
  async sendGift(request: GiftRequest): Promise<ApiResponse<Gift>> {
    await delay(150)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Validate challenge exists
    const challenge = mockDb.findById<Challenge>('challenges', request.challengeId)
    if (!challenge) {
      return error('Challenge not found')
    }

    // Can't gift to yourself
    if (request.recipientUserId === userId) {
      return error('Cannot gift to yourself')
    }

    // Check community-level gifting permissions (if community context provided)
    if (request.communityId && request.recipientUserId) {
      const communityCheck = _checkCommunityGiftingPermission(
        request.communityId,
        userId,
        request.recipientUserId
      )
      if (communityCheck) return error(communityCheck)
    }

    // Check recipient's user-level gift permissions
    if (request.recipientUserId) {
      const permission = mockDb.findOne<GiftPermission>(
        'giftPermissions',
        (p) => p.userId === request.recipientUserId
      )

      if (permission) {
        if (permission.permissionType === 'deny_all') {
          return error('This user is not accepting gifts')
        }
        if (permission.permissionType === 'allow_contacts') {
          if (!permission.allowedUserIds?.includes(userId)) {
            return error('This user only accepts gifts from contacts')
          }
        }
        if (permission.blockedUserIds?.includes(userId)) {
          return error('You cannot send gifts to this user')
        }
      }
    }

    // Create gift
    const expiresAt = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString() // 1 year

    const gift = mockDb.insert<Gift>('gifts', {
      senderUserId: userId,
      recipientUserId: request.recipientUserId,
      recipientEmail: request.recipientEmail,
      challengeId: request.challengeId,
      communityId: request.communityId,
      status: 'pending',
      personalMessage: request.personalMessage,
      sentAt: new Date().toISOString(),
      expiresAt,
    })

    gift.challenge = challenge

    return success(gift)
  },

  /**
   * Accept a gift
   */
  async acceptGift(giftId: UUID): Promise<ApiResponse<Gift>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const gift = mockDb.findById<Gift>('gifts', giftId)
    if (!gift) {
      return error('Gift not found')
    }

    if (gift.recipientUserId !== userId) {
      return error('Not authorized')
    }

    if (gift.status !== 'pending') {
      return error('Gift is no longer pending')
    }

    // Check if expired
    if (new Date(gift.expiresAt) < new Date()) {
      mockDb.update<Gift>('gifts', giftId, { status: 'expired' })
      return error('Gift has expired')
    }

    const updated = mockDb.update<Gift>('gifts', giftId, {
      status: 'accepted',
      acceptedAt: new Date().toISOString(),
    })

    if (!updated) {
      return error('Failed to accept gift')
    }

    // Note: In real impl, this would also create a UserChallenge
    // with the giftId reference

    return success(updated)
  },

  /**
   * Decline a gift
   */
  async declineGift(giftId: UUID): Promise<ApiResponse<Gift>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const gift = mockDb.findById<Gift>('gifts', giftId)
    if (!gift) {
      return error('Gift not found')
    }

    if (gift.recipientUserId !== userId) {
      return error('Not authorized')
    }

    if (gift.status !== 'pending') {
      return error('Gift is no longer pending')
    }

    const updated = mockDb.update<Gift>('gifts', giftId, {
      status: 'declined',
      declinedAt: new Date().toISOString(),
    })

    if (!updated) {
      return error('Failed to decline gift')
    }

    return success(updated)
  },

  /**
   * Cancel a sent gift
   */
  async cancelGift(giftId: UUID): Promise<ApiResponse<void>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const gift = mockDb.findById<Gift>('gifts', giftId)
    if (!gift) {
      return error('Gift not found')
    }

    if (gift.senderUserId !== userId) {
      return error('Not authorized')
    }

    if (gift.status !== 'pending') {
      return error('Gift can only be cancelled while pending')
    }

    mockDb.update<Gift>('gifts', giftId, { status: 'cancelled' })

    return success(undefined)
  },

  // ============================================================================
  // Gift Permissions
  // ============================================================================

  /**
   * Get gift permissions
   */
  async getGiftPermissions(): Promise<ApiResponse<GiftPermission | null>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const permission = mockDb.findOne<GiftPermission>(
      'giftPermissions',
      (p) => p.userId === userId
    )

    return success(permission)
  },

  /**
   * Update gift permissions
   */
  async updateGiftPermissions(data: {
    permissionType: GiftPermissionType
    allowedUserIds?: UUID[]
    blockedUserIds?: UUID[]
  }): Promise<ApiResponse<GiftPermission>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Find existing or create new
    let permission = mockDb.findOne<GiftPermission>(
      'giftPermissions',
      (p) => p.userId === userId
    )

    if (permission) {
      const updated = mockDb.update<GiftPermission>('giftPermissions', permission.id, data)
      if (!updated) {
        return error('Failed to update permissions')
      }
      return success(updated)
    } else {
      permission = mockDb.insert<GiftPermission>('giftPermissions', {
        userId,
        ...data,
      })
      return success(permission)
    }
  },

  // ============================================================================
  // Recipient Search
  // ============================================================================

  /**
   * Search for potential gift recipients
   * When communityId is provided, only returns members of that community.
   */
  async searchRecipients(
    query: string,
    communityId?: UUID
  ): Promise<ApiResponse<GiftRecipientSearchResult[]>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    if (!query || query.length < 2) {
      return success([])
    }

    const lowerQuery = query.toLowerCase()

    let users: User[]

    if (communityId) {
      // Only search within community members
      const memberships = mockDb.findWhere<CommunityMembership>(
        'communityMemberships',
        (m) => m.communityId === communityId && m.status === 'active' && m.userId !== userId
      )
      const memberUserIds = memberships.map((m) => m.userId)

      users = mockDb
        .findWhere<User>(
          'users',
          (u) =>
            memberUserIds.includes(u.id) &&
            (u.displayName.toLowerCase().includes(lowerQuery) ||
              u.email.toLowerCase().includes(lowerQuery))
        )
        .slice(0, 10)
    } else {
      // Search all users
      users = mockDb
        .findWhere<User>(
          'users',
          (u) =>
            u.id !== userId &&
            (u.displayName.toLowerCase().includes(lowerQuery) ||
              u.email.toLowerCase().includes(lowerQuery))
        )
        .slice(0, 10)
    }

    // Check gift permissions for each user
    const results: GiftRecipientSearchResult[] = users.map((user) => {
      let canReceiveGifts = true

      // Check community-level gifting permissions
      if (communityId) {
        const communityCheck = _checkCommunityGiftingPermission(
          communityId,
          userId,
          user.id
        )
        if (communityCheck) {
          canReceiveGifts = false
        }
      }

      // Check user-level permissions
      if (canReceiveGifts) {
        const permission = mockDb.findOne<GiftPermission>(
          'giftPermissions',
          (p) => p.userId === user.id
        )

        if (permission) {
          if (permission.permissionType === 'deny_all') {
            canReceiveGifts = false
          } else if (permission.permissionType === 'allow_contacts') {
            canReceiveGifts = permission.allowedUserIds?.includes(userId) ?? false
          }
          if (permission.blockedUserIds?.includes(userId)) {
            canReceiveGifts = false
          }
        }
      }

      return {
        id: user.id,
        displayName: user.displayName,
        email: user.email,
        avatarUrl: undefined,
        canReceiveGifts,
      }
    })

    return success(results)
  },

  // ============================================================================
  // Gift Reminders
  // ============================================================================

  /**
   * Send a reminder to a gift recipient who hasn't accepted yet
   */
  async sendGiftReminder(giftId: UUID): Promise<ApiResponse<void>> {
    await delay(200)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const gift = mockDb.findById<Gift>('gifts', giftId)
    if (!gift) {
      return error('Gift not found')
    }

    if (gift.senderUserId !== userId) {
      return error('Not authorized')
    }

    if (gift.status !== 'pending') {
      return error('Reminders can only be sent for pending gifts')
    }

    // In real impl, this would trigger an email/notification to the recipient
    return success(undefined)
  },
}

// =============================================================================
// Internal helpers
// =============================================================================

/**
 * Check community-level gifting permission.
 * Returns an error message string if blocked, or null if allowed.
 *
 * Resolution order:
 * 1. Community must have giftingDefault enabled
 * 2. Sender must be an active member
 * 3. Sender's giftingPermissionOverride is checked (null = use default)
 * 4. Recipient must be an active member
 * 5. Recipient's giftingPermissionOverride is checked (null = use default)
 */
function _checkCommunityGiftingPermission(
  communityId: string,
  senderUserId: string,
  recipientUserId: string
): string | null {
  const community = mockDb.findById<Community>('communities', communityId)
  if (!community) return 'Community not found'

  // Check sender membership
  const senderMembership = mockDb.findOne<CommunityMembership>(
    'communityMemberships',
    (m) => m.communityId === communityId && m.userId === senderUserId && m.status === 'active'
  )
  if (!senderMembership) return 'You must be a member to gift within this community'

  // Check recipient membership
  const recipientMembership = mockDb.findOne<CommunityMembership>(
    'communityMemberships',
    (m) => m.communityId === communityId && m.userId === recipientUserId && m.status === 'active'
  )
  if (!recipientMembership) return 'Recipient is not a member of this community'

  // Resolve sender permission: override > community default
  const senderAllowed = senderMembership.giftingPermissionOverride === 'allowed'
    ? true
    : senderMembership.giftingPermissionOverride === 'blocked'
      ? false
      : community.giftingDefault

  if (!senderAllowed) return 'Gifting is not enabled for you in this community'

  // Resolve recipient permission: override > community default
  const recipientAllowed = recipientMembership.giftingPermissionOverride === 'allowed'
    ? true
    : recipientMembership.giftingPermissionOverride === 'blocked'
      ? false
      : community.giftingDefault

  if (!recipientAllowed) return 'This member has gifting disabled in this community'

  return null
}
